import React, { useMemo } from "react";
import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import styled from "@emotion/styled";
import ResultViewToggle from "./ResultViewToggle";
import { formatResultsCountLabel } from "./formatResultsCount";

const Toolbar = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  width: "100%",
}));

const ResultsCounter = styled(Typography)(({ theme }) => ({
  flexShrink: 0,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.3,
  color: theme.palette.text.secondary,
  whiteSpace: "nowrap",
}));

const ActionsRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexWrap: "nowrap",
  flexShrink: 0,
}));

const IconAction = styled(Button)(({ theme }) => ({
  minWidth: 36,
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: 10,
  border: "none",
  boxShadow: "none",
  color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
  backgroundColor: "transparent",
  "&:hover": {
    border: "none",
    boxShadow: "none",
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : alpha(theme.palette.primary.main, 0.08),
  },
  "&:disabled": {
    color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.26)",
    border: "none",
    backgroundColor: "transparent",
  },
}));

function ToolbarActions({
  resultView,
  onResultViewChange,
  onExport,
  isExporting,
  exportDisabled,
  exportTitle,
  onSaveSearch,
  saveSearchDisabled,
  saveSearchLabel,
}) {
  const saveSearchTooltip = saveSearchLabel === "Save search" ? "Save Search" : saveSearchLabel;

  return (
    <ActionsRow data-testid="search-results-toolbar-actions">
        <ResultViewToggle value={resultView} onChange={onResultViewChange} />

        <Tooltip title={exportTitle || "Download results (.csv)"}>
          <span>
            <IconAction
              onClick={onExport}
              disabled={exportDisabled || isExporting}
              aria-label="Export CSV"
            >
              {isExporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon fontSize="small" />}
            </IconAction>
          </span>
        </Tooltip>

        <Tooltip title={saveSearchTooltip}>
          <span>
            <IconAction
              onClick={onSaveSearch}
              disabled={saveSearchDisabled}
              aria-label={saveSearchLabel}
            >
              <SaveIcon fontSize="small" />
            </IconAction>
          </span>
        </Tooltip>
    </ActionsRow>
  );
}

/**
 * Shared results chrome for V2 actions row and V3 top-right toolbar:
 * result count, list/sheet toggle, export, save search.
 *
 * `actionsOnly` — render just the action controls (used inside the V2 bar).
 */
export default function SearchResultsToolbar({
  totalRows = 0,
  resultView,
  onResultViewChange,
  onExport,
  isExporting = false,
  exportDisabled = false,
  exportTitle,
  onSaveSearch,
  saveSearchDisabled = false,
  saveSearchLabel = "Save search",
  actionsOnly = false,
}) {
  const resultsCountLabel = useMemo(() => formatResultsCountLabel(totalRows), [totalRows]);

  const actions = (
    <ToolbarActions
      resultView={resultView}
      onResultViewChange={onResultViewChange}
      onExport={onExport}
      isExporting={isExporting}
      exportDisabled={exportDisabled}
      exportTitle={exportTitle}
      onSaveSearch={onSaveSearch}
      saveSearchDisabled={saveSearchDisabled}
      saveSearchLabel={saveSearchLabel}
    />
  );

  if (actionsOnly) {
    return actions;
  }

  return (
    <Toolbar data-testid="search-results-toolbar">
      <ResultsCounter
        component="div"
        data-testid="results-counter"
        aria-label={`Showing ${resultsCountLabel}`}
      >
        Showing {resultsCountLabel}
      </ResultsCounter>
      <Box sx={{ marginLeft: "auto" }}>{actions}</Box>
    </Toolbar>
  );
}
